"use client"

import { useEffect, useState } from "react"
import { useAtsSource } from "@/contexts/ats-source-context"
import { useExplorer, useTenantAtsSystem } from "@/hooks/use-explorer"
import { useMappingConfig } from "@/hooks/use-mapping-config"
import { useValueMappingSets } from "@/hooks/use-value-mapping-sets"
import { cachedCount as cached, onHubCountsInvalidated } from "@/lib/hub-counts-cache"

export interface HubCounts {
  entities: number | null
  mappings: number | null
  valueSets: number | null
  sources: number | null
  activeSourceCode: string | null
}

/** The numbers on the hub's summary-card tabs; null while loading or unavailable */
export function useHubCounts(): HubCounts {
  const { atsSystem } = useTenantAtsSystem()
  const { sources, selectedSource } = useAtsSource()
  const { loadEntityTypes } = useExplorer()
  const { loadMappingNames } = useMappingConfig()
  const { loadSets } = useValueMappingSets()
  const [entities, setEntities] = useState<number | null>(null)
  const [mappings, setMappings] = useState<number | null>(null)
  const [valueSets, setValueSets] = useState<number | null>(null)
  // Bumped when a page invalidates the counts, so the effects below refetch
  const [version, setVersion] = useState(0)
  useEffect(() => onHubCountsInvalidated(() => setVersion((v) => v + 1)), [])

  useEffect(() => {
    let cancelled = false
    cached("mappings", async () => (await loadMappingNames()).length)
      .then((n) => !cancelled && setMappings(n))
      .catch(() => !cancelled && setMappings(null))
    cached("value-sets", async () => (await loadSets()).length)
      .then((n) => !cancelled && setValueSets(n))
      .catch(() => !cancelled && setValueSets(null))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version])

  useEffect(() => {
    if (!atsSystem) return
    let cancelled = false
    cached(`entities:${atsSystem}`, async () => (await loadEntityTypes(atsSystem)).length)
      .then((n) => !cancelled && setEntities(n))
      .catch(() => !cancelled && setEntities(null))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atsSystem, version])

  return { entities, mappings, valueSets, sources: sources.length, activeSourceCode: selectedSource?.code ?? null }
}
